import { ArrowLeftRight, AlertTriangle } from 'lucide-react'
import { useEffect } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  SwapArrow,
  getNetworkName,
  cn,
} from '@valiant/ui-shared'
import { toast } from 'sonner'
import { useNavigation } from '../../../../contexts/NavigationContext'
import { MintSelector } from '../../MintSelector'
import { AmountInput } from './AmountInput'
import { QuoteDisplay } from './QuoteDisplay'
import { useMints, useQuote, useSwapForm } from './hooks'
import type { SwapFormProps } from './types'
import { useGetMaintenanceModeQuery } from '../../../../generated/graphql'

export function SwapForm({ className, onSwapSubmit, onReset }: SwapFormProps) {
  const { selectedCompany } = useNavigation()

  // Check maintenance mode
  const [{ data: maintenanceData }] = useGetMaintenanceModeQuery()
  const isMaintenanceMode = maintenanceData?.getMaintenanceMode ?? false

  // Custom hooks for data and logic
  const {
    availableMints,
    availableMintsWithBalance,
    withdrawalAddresses,
    allWithdrawalAddresses,
    getToMintsFor,
    isLoading,
    balanceData,
  } = useMints()

  const {
    fromMintId,
    setFromMintId,
    toMintId,
    setToMintId,
    fromAmount,
    setFromAmount,
    selectedAddressId,
    setSelectedAddressId,
    selectedPercentage,
    setSelectedPercentage,
    fromMintBalance,
    isAmountGreaterThanBalance,
    fromMint,
    toMint,
    filteredWithdrawalAddresses,
    withdrawalAddressStatus,
    handlePercentageClick,
    resetForm,
  } = useSwapForm({
    availableMints,
    withdrawalAddresses,
    allWithdrawalAddresses,
    balanceData,
  })

  useEffect(() => {
    if (onReset) {
      onReset(resetForm)
    }
  }, [resetForm, onReset])

  const {
    currentQuote,
    quoteCountdown,
    quoteFetching,
    quoteStatus,
    failureRetryAt,
    shouldFetchQuote,
  } = useQuote({
    fromMintId,
    toMintId,
    fromAmount,
    selectedCompanyId: selectedCompany?.id || '',
    isAmountGreaterThanBalance,
  })

  const isFiatDestination = toMint?.chain === 'FIAT'

  const handleSwapClick = () => {
    if (!fromMintId || !toMintId || !fromAmount) {
      toast.error('Please fill in all required fields')
      return
    }
    if (!isFiatDestination && !selectedAddressId) {
      toast.error('Please fill in all required fields')
      return
    }
    onSwapSubmit({
      fromMintId,
      toMintId,
      fromAmount,
      selectedAddressId,
      currentQuote,
      selectedCompanyId: selectedCompany?.id || '',
    })
  }

  const handleFromMintChange = (value: string) => {
    setFromMintId(value)
    setFromAmount('0')
    setSelectedPercentage(0)
    // Clear toMintId if it's no longer a valid pair for the new fromMint
    const allowedTo = getToMintsFor(value)
    if (toMintId && !allowedTo.some((m) => m.id === toMintId)) {
      setToMintId('')
    }
  }

  return (
    <div className={cn('flex flex-col h-full p-5', className)}>
      {isMaintenanceMode && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
          <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
          <span className="text-sm font-medium text-orange-800">
            App in maintenance. No trades allowed.
          </span>
        </div>
      )}

      <div className="mb-8 md:mb-12.5">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5" />
          <h3 className="font-semibold text-2xl">Swap</h3>
        </div>
        <p className="text-2xl text-card-text mt-5 leading-none font-bold">
          {selectedCompany?.company}
        </p>
      </div>

      <div className="flex flex-col justify-between h-full pb-4">
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-10 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : availableMints.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ArrowLeftRight className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-medium mb-1">No currencies available</p>
            <p className="text-sm">This company has no currencies configured for swapping.</p>
          </div>
        ) : availableMintsWithBalance.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <ArrowLeftRight className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="font-medium mb-1">No holdings available</p>
            <p className="text-sm">This company has no holdings for swapping.</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              <MintSelector
                value={fromMintId}
                onValueChange={handleFromMintChange}
                mints={availableMintsWithBalance}
                label="From"
                placeholder="Select currency"
              />

              <AmountInput
                value={fromAmount}
                onChange={setFromAmount}
                balance={fromMintBalance}
                symbol={fromMint?.symbol || ''}
                onPercentageClick={handlePercentageClick}
                selectedPercentage={selectedPercentage}
              />

              <div className="flex justify-center mt-4 md:mt-0">
                <SwapArrow className="rotate-90" />
              </div>

              <MintSelector
                value={toMintId}
                onValueChange={setToMintId}
                mints={getToMintsFor(fromMintId)}
                label="To"
                placeholder="Select Coin"
                excludeId={fromMintId}
              />

              {toMint && (
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-2 block">Network</label>
                  <div className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm font-medium text-gray-900">
                    {getNetworkName(toMint.chain)}
                  </div>
                </div>
              )}

              {shouldFetchQuote && (
                <div>
                  <label className="text-xs font-medium text-gray-700 mb-2 block">
                    Estimated return
                  </label>
                  <QuoteDisplay
                    quote={currentQuote}
                    toMint={toMint}
                    fromMint={fromMint}
                    countdown={quoteCountdown}
                    isLoading={quoteFetching || quoteStatus === 'PENDING'}
                    retryAt={failureRetryAt}
                  />
                </div>
              )}
            </div>

            <div>
              {withdrawalAddressStatus === 'none' && toMint && !isFiatDestination && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-orange-800">
                    Please add a whitelisted withdrawal address first
                  </span>
                </div>
              )}

              {withdrawalAddressStatus === 'pending' && toMint && !isFiatDestination && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-orange-200 bg-orange-50 px-4 py-3">
                  <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-orange-800">
                    Please ask a Group Owner to approve requested address
                  </span>
                </div>
              )}

              {filteredWithdrawalAddresses.length > 0 && !isAmountGreaterThanBalance && (
                <div className="mb-7">
                  <label className="text-xs font-medium text-gray-700 mb-2 block">
                    Withdrawal Address
                  </label>
                  <Select value={selectedAddressId} onValueChange={setSelectedAddressId}>
                    <SelectTrigger className="w-full px-4.5 py-2.5" size="lg">
                      <SelectValue placeholder="Select address" />
                    </SelectTrigger>
                    <SelectContent>
                      {filteredWithdrawalAddresses.map((address) => (
                        <SelectItem key={address.id} value={address.id}>
                          <div className="text-left">
                            <div className="font-medium">{address.label}</div>
                            <div className="text-sm text-gray-500">
                              {address.address.slice(0, 10)}...{address.address.slice(-8)}
                            </div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                className="w-full h-[40px]"
                size="lg"
                onClick={handleSwapClick}
                disabled={
                  isMaintenanceMode ||
                  !fromMintId ||
                  !toMintId ||
                  !fromAmount ||
                  (!isFiatDestination && !selectedAddressId) ||
                  isAmountGreaterThanBalance ||
                  !currentQuote
                }
              >
                <ArrowLeftRight className="w-5 h-5 mr-2" />
                Swap
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
